/**
 * Hero Trouvé intent — persistence helper.
 *
 * The hero chat captures up to 2 conversational turns (topic + optional
 * free-text + chip selections + Trouvé's responses) before sending the
 * visitor to /login → /onboarding. The captured intent travels with
 * them via sessionStorage so:
 *   • Onboarding can SKIP the "What's your goal?" question (the user
 *     already declared it via the topic chip).
 *   • The generated routine is enriched with the topic + free-text
 *     so the first AI pass reflects what the user actually asked for.
 *   • A login/signup detour doesn't lose the conversation state.
 *
 * Stored as a single JSON blob under `HERO_INTENT_KEY`. sessionStorage
 * (not localStorage) — by design we clear it once consumed and we
 * never want stale intent leaking across tabs / days.
 *
 * Shape:
 *   {
 *     topic_id: "beard_shadow",
 *     topic_label: "Beard shadow coverage",
 *     turn1_chip_id: "showing_through_foundation" | null,
 *     turn1_chip_label: "Showing through foundation" | null,
 *     free_text: "..." | null,           // anything they typed
 *     transcript: [                       // human-readable replay
 *       { role: "trouve", text: "Hey, I'm Trouvé..." },
 *       { role: "user",   text: "Beard shadow coverage" },
 *       { role: "trouve", text: "Beautiful..." },
 *       { role: "user",   text: "Showing through foundation" },
 *       { role: "trouve", text: "That usually means..." }
 *     ],
 *     captured_at: "2026-06-25T13:42:11.123Z",
 *     goal: "everyday"                    // derived via topicToGoal()
 *   }
 */
const HERO_INTENT_KEY = "trubeaute.heroIntent.v1";

const _safe = (fn) => {
  try { return fn(); } catch { return null; }
};

export const saveHeroIntent = (intent) => {
  _safe(() => {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    window.sessionStorage.setItem(HERO_INTENT_KEY, JSON.stringify(intent));
  });
};

export const readHeroIntent = () => {
  return _safe(() => {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(HERO_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Defensive: minimum sanity check so a corrupt blob can't crash
    // Onboarding's hydration path.
    if (!parsed || typeof parsed !== "object" || !parsed.topic_id) return null;
    return parsed;
  });
};

export const clearHeroIntent = () => {
  _safe(() => {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    window.sessionStorage.removeItem(HERO_INTENT_KEY);
  });
};

export const HERO_INTENT_STORAGE_KEY = HERO_INTENT_KEY;
